import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ItemApiService } from "src/services/item/item-api.service";
import { UserApiService } from "src/services/user/user-api.service";
import { Item } from "src/services/item/item.interface";
import { User, userToString } from "src/services/user/user.interface";
import { SubscriptionApiService } from "src/services/subscription/subscription-api.service";
import { Subscription } from "src/services/subscription/subscription";
import { MessageService } from "src/services/message/message.service";
import { baseLocationToString } from "src/services/subscription/base-locations.enum";
import { Rack, rackToString } from "src/services/item/rack.interface";
import { Vehicle, vehicleToString } from "src/services/item/vehicle.interface";
import { LabelPrintingService } from "src/services/qr-code/label-printing.service";
import { LabelFormatName, printDialogHint } from "src/services/qr-code/label-format.const";
import {
  LabelContent,
  buildItemLabelContent,
  buildRackLabelContent,
  buildVehicleLabelContent,
} from "src/services/qr-code/label-content.interface";

@Component({
  selector: "rc-qr-code",
  templateUrl: "./qr-code.component.html",
  styleUrls: ["./qr-code.component.scss"],
  standalone: false,
})
export class QrCodeComponent implements OnInit {
  @Input() public car: Vehicle | undefined;
  @Input() public itemInput: string | Item | undefined;
  @Input() public rack: Rack | undefined;
  @Input() public showPrintButton = true;
  @Input() public showPrintHints = true;
  @Input() public subscription: Subscription | undefined;
  @Input() public user: User | undefined;

  @Output() public onDismiss = new EventEmitter<void>();

  public item: Item;
  public loading = true;
  public qrCodeDataUrl: string;

  private labelContent: LabelContent;

  constructor(
    private readonly messageService: MessageService,
    private readonly itemService: ItemApiService,
    private readonly labelPrintingService: LabelPrintingService,
    private readonly subscriptionService: SubscriptionApiService,
    private readonly userService: UserApiService,
  ) {}

  public async ngOnInit(): Promise<void> {
    try {
      if (this.itemInput) {
        await this.ngOnInitForItem(this.itemInput);
      } else if (this.rack) {
        this.labelContent = buildRackLabelContent(this.rack);
      } else if (this.car) {
        this.labelContent = buildVehicleLabelContent(this.car);
      }

      if (this.labelContent) {
        this.qrCodeDataUrl = await this.labelPrintingService.renderPreviewDataUrl(this.labelContent.qrPayload);
      }
    } catch (error) {
      await this.messageService.presentUnexpectedErrorToast("The QR code could not be generated.", error);
    } finally {
      this.loading = false;
    }
  }

  public get canPrint(): boolean {
    return !this.loading && !!this.labelContent && this.labelPrintingService.isAvailable;
  }

  public get printAvailable(): boolean {
    return this.showPrintButton && this.labelPrintingService.isAvailable;
  }

  public async printBrotherLabel(): Promise<void> {
    await this.labelPrintingService.print(this.printableLabels(), LabelFormatName.brotherLabel);
  }

  public async printDymoLabel(): Promise<void> {
    await this.labelPrintingService.print(this.printableLabels(), LabelFormatName.dymoLabelWriter);
  }

  private printableLabels(): LabelContent[] {
    return this.labelContent ? [this.labelContent] : [];
  }

  private async ngOnInitForItem(itemInput: string | Item): Promise<void> {
    this.item = typeof itemInput === "string" ? await this.itemService.getById(itemInput) : itemInput;

    if (!this.user) {
      this.user = await this.userService.getById(this.item.userId);
    }
    if (!this.subscription) {
      this.subscription = await this.subscriptionService.getFirstByUserId(this.user.id);
    }

    this.labelContent = buildItemLabelContent(this.item, this.user, this.subscription);
  }

  public readonly brotherHint = printDialogHint(LabelFormatName.brotherLabel);
  public readonly dymoHint = printDialogHint(LabelFormatName.dymoLabelWriter);
  public readonly userToString = userToString;
  public readonly baseLocationToString = baseLocationToString;
  public readonly rackToString = rackToString;
  public readonly carToString = vehicleToString;
}
